import React, {
    type Dispatch,
    type SetStateAction, useCallback,
} from 'react';


import {DateRangeInput} from 'src/components/input';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl} from 'src/contexts/validation-context';

import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {
    END_DATE_CONTROL,
    START_DATE_CONTROL,
} from '../constants';

import {ConferenceNameInput} from './conference-name-input';

import type {CreateConferenceData} from '../useCreateConference';

const SECTION_NAME = 'Conference Information';

type Props = {
    data: CreateConferenceData,
    setData: Dispatch<SetStateAction<CreateConferenceData>>,
    trackParameterChange: (parameter: string) => void,
};

export const ConferenceInformation = (props: Props) => {
    const {data, setData, trackParameterChange} = props;

    const changeConferenceName = useCallback((conferenceName: string) => {
        setData(prevState => ({...prevState, conferenceName}));
        trackParameterChange('Conference Name');
    }, [setData, trackParameterChange]);

    const changeStartDate = useCallback((date: string) => {
        setData(prev => ({...prev, confStartDate: date}));
        trackParameterChange('Start Date');
    }, [setData, trackParameterChange]);

    const changeEndDate = useCallback((date: string) => {
        setData(prev => ({...prev, confEndDate: date}));
        trackParameterChange('End Date');
    }, [setData, trackParameterChange]);

    return (
        <FormSection headline={SECTION_NAME}>
            <Group direction="v">
                <WidthItem width={320}>
                    <Label>Conference name</Label>
                    <ConferenceNameInput
                        conferenceName={data.conferenceName}
                        onChange={changeConferenceName}
                    />
                </WidthItem>
                <div>
                    <Label>Conference Period</Label>
                    <Group direction="v" gap="s">
                        <ValidatedControl name={START_DATE_CONTROL}>
                            {(invalid: boolean) => (
                                <WidthItem width={320}>
                                    <DateRangeInput
                                        id="conference-start-date-picker"
                                        width={320}
                                        label="Starts"
                                        invalid={invalid}
                                        clearable={true}
                                        startDate={data.confStartDate}
                                        endDate={data.confEndDate}
                                        selectRangeType="start"
                                        onChange={changeStartDate}
                                    />
                                </WidthItem>
                            )}
                        </ValidatedControl>
                        <ValidatedControl name={END_DATE_CONTROL}>
                            {(invalid: boolean) => (
                                <WidthItem width={320}>
                                    <DateRangeInput
                                        id="conference-end-date-picker"
                                        width={320}
                                        label={<>Ends&nbsp;&nbsp;</>}
                                        invalid={invalid}
                                        clearable={true}
                                        preventPastDate={true}
                                        startDate={data.confStartDate}
                                        endDate={data.confEndDate}
                                        selectRangeType="end"
                                        onChange={changeEndDate}
                                    />
                                </WidthItem>
                            )}
                        </ValidatedControl>
                    </Group>
                </div>
            </Group>
        </FormSection>
    );
};
